import { useMount } from 'ahooks';
import { message, PaginationProps } from 'antd';
import useCrud from 'hooks/useCrud';
import { useCallback, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { caseList, CaseListParams, CaseListResponse } from 'services/api/feedback';

export type CaseStatusType = 'all' | 'repairing' | 'repaired' | 'closed';

export const caseStatusMap: Record<CaseStatusType, Feedback.CaseStatus[]> = {
  all: ['NEW', 'PROCESSING', 'TESTING', 'FIXED', 'CLOSED'],
  repairing: ['NEW', 'PROCESSING', 'TESTING'],
  repaired: ['FIXED'],
  closed: ['CLOSED'],
};

const getCountMap = (counts: CaseListResponse['caseStatusCount']) => {
  const res: Record<CaseStatusType, number> = {
    all: 0,
    repairing: 0,
    repaired: 0,
    closed: 0,
  };

  counts.forEach(({ caseStatus, count }) => {
    Object.keys(caseStatusMap).forEach((statusType) => {
      if (caseStatusMap[statusType].includes(caseStatus)) {
        res[statusType] += count;
      }
    });
  });

  return res;
};

function useCaseList(initialFetch: boolean = true) {
  const { t } = useTranslate();
  const latestListRequest = useRef(0);

  const [statusType, setStatusType] = useState<CaseStatusType>('all');

  const [page, setPage] = useState<PaginationProps>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [countMap, setCountMap] = useState<Record<CaseStatusType, number>>(() => getCountMap([]));

  const res = useCrud({
    readList: useCallback(
      (params: Omit<CaseListParams, 'caseStatus'> & { statusType: CaseStatusType }) => {
        const { statusType, feedback, ...others } = params;
        const requestId = ++latestListRequest.current;

        return caseList({
          ...others,
          feedback,
          caseStatus: caseStatusMap[statusType],
        }).then((res) => {
          const { caseBasicInfo, caseStatusCount, pageNum: current, pageSize } = res;
          // useCrud 已保护列表内容；页签、分页和计数同样只接受最后一次请求。
          if (requestId !== latestListRequest.current) {
            return caseBasicInfo;
          }

          setStatusType(statusType);

          const countMap = getCountMap(caseStatusCount);

          const { [statusType]: total } = countMap;

          setCountMap(countMap);

          setPage({
            current,
            pageSize,
            total,
          });

          return caseBasicInfo;
        });
      },
      [],
    ),
  });

  const { fetchList } = res;

  useMount(() => {
    if (!initialFetch) {
      return;
    }

    caseList({
      caseStatus: caseStatusMap.all,
      pageNum: 1,
      pageSize: 1,
    })
      .then((res) => {
        // 初始计数请求可能晚于主动刷新返回，不再用默认页签覆盖用户这次跳转。
        if (latestListRequest.current > 0) {
          return;
        }
        const { caseStatusCount } = res;

        const countMap = getCountMap(caseStatusCount);

        const hasRepairedCase = countMap.repaired > 0;

        return fetchList({
          statusType: hasRepairedCase ? 'repaired' : 'all',
          pageNum: 1,
          pageSize: 10,
        });
      })
      .catch(() => {
        message.error(t('feedback.获取反馈数据失败，请稍后再试'));
      });
  });

  return {
    ...res,
    statusType,
    setStatusType,
    page,
    setPage,
    countMap,
    setCountMap,
  };
}

export default useCaseList;
